import { i18n } from '@quadralize/i18n'
import { cn } from '@quadralize/utils'
import type * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '../../overlays/tooltip/tooltip'
import { MOBILE_BLOCKED_CARD_VERTICAL_OFFSET, MOBILE_SLOT_H } from './constants'
import type {
  BlockedSlot,
  SchedulerDensity,
  SchedulerProps,
  SchedulerSlotDuration,
  SchedulerView,
} from './types'
import type { TimeSlot } from './utils'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SlotRowProps {
  slot: TimeSlot
  columnId: string
  columnDate?: string
  showLabel: boolean
  isNow: boolean
  activeDate: Date
  view: SchedulerView
  density?: SchedulerDensity
  slotDuration: SchedulerSlotDuration
  resource?: SchedulerProps['resources'][number]
  onSlotClick?: SchedulerProps['onSlotClick']
  onBlockedSlotClick?: (slot: BlockedSlot) => void
  renderSlot?: SchedulerProps['renderSlot']
}

// ─── Component ────────────────────────────────────────────────────────────────

export const SlotRow = ({
  slot,
  columnId,
  columnDate,
  showLabel,
  isNow,
  activeDate,
  view,
  density = 'comfortable',
  slotDuration,
  resource,
  onSlotClick,
  onBlockedSlotClick,
  renderSlot,
}: SlotRowProps): React.JSX.Element => {
  const { t } = useTranslation('ui', { i18n })
  const isClickable = !slot.blocked && !slot.isOffHours && slot.events.length === 0
  const isBlockedClickable = !!slot.blocked && !!onBlockedSlotClick
  const isCompact = density === 'compact'

  return (
    <div
      className={cn('flex gap-2 relative', slot.isOffHours && 'opacity-45')}
      style={{ minHeight: `${MOBILE_SLOT_H}px` }}
    >
      {/* Time label column */}
      <div className="w-11 shrink-0 flex flex-col items-end pt-1 relative">
        {showLabel && (
          <span
            className={cn(
              'font-medium tabular-nums text-text-tertiary leading-tight',
              isCompact ? 'text-[10px]' : 'text-[11px]',
            )}
          >
            {slot.timeStr}
          </span>
        )}
        {/* Vertical timeline line */}
        <div className="absolute right-3 top-0 bottom-0 w-px bg-border-subtle" />
        {/* Now dot */}
        {isNow && (
          <div className="absolute right-2.5 top-3 z-10">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error opacity-50" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-error" />
            </span>
          </div>
        )}
      </div>

      {/* Content column */}
      <div className="flex-1 min-w-0 py-1 pr-3 flex flex-col gap-1.5 relative">
        {isNow && (
          <div className="absolute left-0 right-0 top-3 h-px bg-error/70 z-10 pointer-events-none" />
        )}
        {/* Blocked slot */}
        {slot.isBlockedStart && slot.blocked && (
          <button
            type="button"
            aria-label={
              slot.blocked.reason
                ? t('scheduler.blockedSlotReason', { reason: slot.blocked.reason })
                : t('scheduler.blockedSlot')
            }
            disabled={!isBlockedClickable}
            onClick={() => isBlockedClickable && slot.blocked && onBlockedSlotClick?.(slot.blocked)}
            className={cn(
              'absolute inset-x-0 top-1 z-10 mx-1 rounded-lg px-3 py-2 flex items-center gap-2 overflow-hidden text-left',
              'border border-dashed border-border-strong/40',
              isBlockedClickable
                ? 'cursor-pointer hover:brightness-95 transition-[filter] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45'
                : 'cursor-default',
            )}
            style={{
              height: `${(slot.blockedDuration / slotDuration) * MOBILE_SLOT_H - MOBILE_BLOCKED_CARD_VERTICAL_OFFSET}px`,
              backgroundImage:
                'repeating-linear-gradient(135deg, transparent 0px, transparent 4px, rgba(128,128,128,0.06) 4px, rgba(128,128,128,0.06) 8px)',
            }}
          >
            <span className="text-[11px] text-text-tertiary font-medium truncate">
              {slot.blocked.reason ?? t('scheduler.blockedSlot')}
            </span>
            <span className="text-[10px] text-text-tertiary/70 tabular-nums ml-auto shrink-0">
              {slot.blocked.startTime} – {slot.blocked.endTime}
            </span>
          </button>
        )}

        {/* Off-hours indicator */}
        {slot.isOffHoursStart && (
          <div
            className="absolute inset-x-0 top-1 z-10 mx-1 rounded-lg px-3 py-1.5 bg-bg-subtle border border-border-subtle flex items-center overflow-hidden"
            style={{
              height: `${(slot.offHoursDuration / slotDuration) * MOBILE_SLOT_H - MOBILE_BLOCKED_CARD_VERTICAL_OFFSET}px`,
            }}
          >
            <span className="text-[10px] text-text-tertiary">{t('scheduler.outsideHours')}</span>
          </div>
        )}

        {/* Add slot button */}
        {isClickable && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`Agendar às ${slot.timeStr}`}
                onClick={() => onSlotClick?.(columnId, slot.timeStr, columnDate)}
                className={cn(
                  'w-full rounded-lg border border-dashed border-border-default/60 bg-bg-base/70',
                  'flex items-center justify-center gap-1.5',
                  isCompact ? 'text-[10px]' : 'text-[11px]',
                  'font-medium text-text-tertiary',
                  'opacity-0 hover:opacity-100 focus-visible:opacity-100',
                  'hover:border-primary/35 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 active:bg-bg-subtle transition-all',
                  'min-h-10',
                )}
              >
                {renderSlot?.({
                  resourceId: columnId,
                  resource,
                  time: slot.timeStr,
                  date: columnDate,
                  view,
                  activeDate,
                  isMobile: true,
                }) ?? (
                  <>
                    <span className="text-sm leading-none text-primary/60">+</span>
                    <span className="text-primary/60">{slot.timeStr}</span>
                  </>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>
                Reservar às <strong>{slot.timeStr}</strong>
              </p>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

SlotRow.displayName = 'SlotRow'
